/**
 * LoginFormRedesign - Documentary Editorial aesthetic
 *
 * Warm, authentic sign-in form matching the WEattitude.org
 * landing page redesign.
 */

import { FormikProps, Form } from "formik";
import { memo, useState } from "react";
import { Link } from "react-router-dom";
import { APP_ROUTES } from "const";
import { FormikInput } from "components";
import AuthPanelRedesign from "components/AuthPanel/AuthPanelRedesign";
import { ValuesType } from "./LoginForm.types";
import { FIELD_NAMES, MAX_FORM_FIELDS_LENGTH } from "./LoginForm.constants";

interface LoginFormRedesignProps
  extends FormikProps<ValuesType>,
    React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

// Custom styled button for the editorial aesthetic
function EditorialButton({
  children,
  disabled,
  type = "button",
}: {
  children: React.ReactNode;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      type={type}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        relative w-full py-3.5 px-6 rounded-lg font-medium text-base
        transition-all duration-300 ease-out
        ${disabled
          ? 'bg-stone-300 text-stone-500 cursor-not-allowed'
          : 'text-white cursor-pointer'
        }
      `}
      style={{
        backgroundColor: disabled ? undefined : (isHovered ? '#A13508' : '#C2410C'),
        fontFamily: "'Source Sans 3', sans-serif",
        boxShadow: disabled ? 'none' : (
          isHovered
            ? '0 10px 30px -5px rgba(194, 65, 12, 0.4)'
            : '0 4px 15px -3px rgba(194, 65, 12, 0.3)'
        ),
        transform: isHovered && !disabled ? 'translateY(-1px)' : 'translateY(0)',
      }}
    >
      {children}
    </button>
  );
}

// Custom styled input wrapper for editorial aesthetic
function EditorialInputWrapper({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label
        className="block text-sm font-medium tracking-wide"
        style={{
          color: '#44403C',
          fontFamily: "'Source Sans 3', sans-serif",
        }}
      >
        {label}
      </label>
      <div className="relative">
        {children}
      </div>
    </div>
  );
}

function LoginFormRedesign(props: LoginFormRedesignProps) {
  return (
    <AuthPanelRedesign variant="signin">
      {/* Headline */}
      <h1
        className="mt-6 text-3xl lg:text-4xl leading-tight"
        style={{
          color: '#1C1917',
          fontFamily: "'Fraunces', serif",
          fontWeight: 400,
        }}
      >
        Welcome back
      </h1>
      <p
        className="mt-3 text-base"
        style={{
          color: '#78716C',
          fontFamily: "'Source Sans 3', sans-serif",
        }}
      >
        Sign in to continue making an impact.
      </p>

      {/* Sign up link */}
      <div
        className="mt-4 text-sm"
        style={{
          color: '#78716C',
          fontFamily: "'Source Sans 3', sans-serif",
        }}
      >
        New to WEattitude?{' '}
        <Link
          to={APP_ROUTES.SIGN_UP}
          className="font-semibold transition-colors hover:underline"
          style={{ color: '#C2410C' }}
        >
          Create an account
        </Link>
      </div>

      {/* Form */}
      <Form className="mt-8 space-y-5">
        <EditorialInputWrapper label="Email address">
          <FormikInput
            autoComplete="email"
            name={FIELD_NAMES.EMAIL}
            placeholder="you@example.com"
            className="!rounded-[6px] !border-0 !bg-white !px-4 !py-3 focus:!border-[#C2410C] focus:!ring-[#C2410C]/20 !transition-all !w-full"
            style={{ fontFamily: "'Source Sans 3', sans-serif" }}
          />
        </EditorialInputWrapper>

        <EditorialInputWrapper label="Password">
          <FormikInput
            autoComplete="current-password"
            maxLength={MAX_FORM_FIELDS_LENGTH}
            name={FIELD_NAMES.PASSWORD}
            type="password"
            placeholder="••••••••"
            className="!rounded-[6px] !border-0 !bg-white !px-4 !py-3 focus:!border-[#C2410C] focus:!ring-[#C2410C]/20 !transition-all !w-full"
            style={{ fontFamily: "'Source Sans 3', sans-serif" }}
          />
        </EditorialInputWrapper>

        {/* Forgot password */}
        <div className="flex justify-end">
          <Link
            to={APP_ROUTES.FORGET_PASSWORD}
            className="text-sm font-medium transition-colors hover:underline"
            style={{
              color: '#C2410C',
              fontFamily: "'Source Sans 3', sans-serif",
            }}
          >
            Forgot your password?
          </Link>
        </div>

        {/* Submit button */}
        <div className="pt-2">
          <EditorialButton type="submit" disabled={props.isSubmitting}>
            {props.isSubmitting ? 'Signing in...' : 'Sign in'}
          </EditorialButton>
        </div>
      </Form>

    </AuthPanelRedesign>
  );
}

export default memo(LoginFormRedesign);
